import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, MessageSquareReply, Pencil, ReceiptText, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface HandbackButton {
  id: string;
  title: string;
}

export interface HandbackPayload {
  message?: string;
  buttons?: HandbackButton[];
  /**
   * Have the agent write the next message itself. Only meaningful without a `message`: when the
   * operator closes with their own line, the next turn belongs to the customer.
   */
  requestAgentTurn?: boolean;
}

interface HandbackDialogProps {
  open: boolean;
  /** Prefilled from what the cart still needs, so the operator rarely has to write anything. */
  suggestion: string;
  /**
   * The priced cart, exactly as the agent would present it. Absent when there is nothing worth
   * summarizing (an empty cart), which is also when the summary preset is not offered.
   */
  cartSummary?: string;
  /**
   * There are staged edits that have not been saved, so `cartSummary` is not yet the cart the
   * operator is looking at. The preset is still reachable — it just says so first, because sending a
   * summary that omits the change you just made is the one bad outcome here.
   */
  summaryStale?: boolean;
  /**
   * Offer the "Sí, confirmar" quick reply — true when the cart is placeable and the only thing
   * missing is the customer's go-ahead. Offering it on a cart that still has blockers would invite
   * a tap the agent then has to refuse.
   */
  offerConfirmButton?: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (payload: HandbackPayload) => void;
}

/** WhatsApp truncates quick-reply titles past 20 characters. */
const MAX_BUTTON_TITLE = 20;

type HandbackMode = 'summary' | 'custom';

/**
 * Hand the conversation back to the agent — the three ways an operator's turn can end.
 *
 * **The order summary** (the default when the cart is saved and ready): the priced cart plus the one
 * question left, with quick replies attached. This is the common case by a distance — the operator
 * fixed the cart and needs a yes.
 *
 * **A message of their own**: anything the situation actually calls for. Either way the message goes
 * out as the assistant, so the agent reads it as its own previous turn and picks up from whatever the
 * customer answers — which is why a question is the useful thing to send here, not a sign-off. A tap
 * on a quick reply arrives as an ordinary customer message, so "Sí, confirmar" reaches the freshly
 * resumed agent exactly like a typed "sí"; it saves the customer from typing, it does not confirm the
 * order behind the agent.
 *
 * **Nothing at all**, and the agent takes the next turn: releasing the AI on its own would leave the
 * conversation silent, because the agent only speaks in answer to a customer message and the last
 * word here was the operator's. `requestAgentTurn` is what makes it write.
 */
export function HandbackDialog({
  open,
  suggestion,
  cartSummary,
  summaryStale,
  offerConfirmButton,
  submitting,
  onCancel,
  onSubmit,
}: HandbackDialogProps) {
  const { t } = useTranslation('conversations');

  const summaryMessage = cartSummary
    ? [t('cart.handback.summaryIntro'), '', cartSummary, '', t('cart.handback.summaryQuestion')].join('\n')
    : undefined;

  const [mode, setMode] = useState<HandbackMode>('custom');
  const [message, setMessage] = useState(suggestion);
  const [withButtons, setWithButtons] = useState(false);

  useEffect(() => {
    if (!open) return;
    // A stale summary is offered but not preselected: the operator should see the cart they staged,
    // not be handed a message that predates it.
    const initial: HandbackMode = summaryMessage && !summaryStale ? 'summary' : 'custom';
    setMode(initial);
    setMessage(initial === 'summary' ? (summaryMessage as string) : suggestion);
    setWithButtons(Boolean(offerConfirmButton));
    // Re-prefilling on every summary keystroke upstream would fight the operator's own edits; this
    // runs when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectMode = (next: HandbackMode) => {
    setMode(next);
    setMessage(next === 'summary' ? (summaryMessage ?? '') : suggestion);
  };

  // Truncated HERE, not just where the chips are drawn: the server caps `title` at 20 characters, so
  // a translation that grew past it would turn the hand-back into a 400 rather than a shorter label.
  const buttons: HandbackButton[] = [
    { id: 'confirm_order', title: t('cart.handback.buttons.confirm').slice(0, MAX_BUTTON_TITLE) },
    { id: 'change_order', title: t('cart.handback.buttons.change').slice(0, MAX_BUTTON_TITLE) },
  ];

  const attach = Boolean(offerConfirmButton) && withButtons;
  const trimmed = message.trim();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('cart.handback.title')}</DialogTitle>
          <DialogDescription>{t('cart.handback.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto">
          {summaryMessage && (
            <div className="flex gap-1.5">
              <ModeButton
                active={mode === 'summary'}
                icon={<ReceiptText className="h-3.5 w-3.5" />}
                label={t('cart.handback.modes.summary')}
                onClick={() => selectMode('summary')}
              />
              <ModeButton
                active={mode === 'custom'}
                icon={<Pencil className="h-3.5 w-3.5" />}
                label={t('cart.handback.modes.custom')}
                onClick={() => selectMode('custom')}
              />
            </div>
          )}

          {mode === 'summary' && summaryStale && (
            <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{t('cart.handback.summaryStale')}</span>
            </p>
          )}

          <Textarea
            rows={mode === 'summary' ? 9 : 3}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('cart.handback.placeholder')}
          />

          {offerConfirmButton && (
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="cart-handback-buttons"
                  checked={withButtons}
                  onCheckedChange={(next) => setWithButtons(next === true)}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="cart-handback-buttons" className="text-sm font-normal">
                    {t('cart.handback.buttons.label')}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t('cart.handback.buttons.hint')}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 pl-6">
                {buttons.map((button) => (
                  <span
                    key={button.id}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-opacity',
                      'border-border bg-background text-foreground',
                      !withButtons && 'opacity-40',
                    )}
                  >
                    <MessageSquareReply className="h-3 w-3" />
                    {button.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t('cart.handback.withoutMessageHint')}</p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onSubmit({ requestAgentTurn: true })}
            disabled={submitting}
          >
            {t('cart.handback.withoutMessage')}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('cart.actions.cancel')}
            </Button>
            <Button
              type="button"
              loading={submitting}
              disabled={!trimmed}
              onClick={() => onSubmit({ message: trimmed, buttons: attach ? buttons : undefined })}
            >
              <Bot className="h-3.5 w-3.5" />
              {t('cart.handback.send')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
